import { CalendarPlus, Copy, Download, Link2 } from 'lucide-react';
import { useMemo, useState, type FormEvent } from 'react';
import { formatMeetingId, LIMITS, parseMeetingInput, type CreateMeetingResponse } from '../../shared/protocol';
import { Button, Modal } from '../components/ui';
import { api, joinUrl } from '../lib/api';
import { copyWithFeedback } from '../lib/share';
import { downloadIcs, inviteText } from '../lib/invite';
import { setJoinPrefs } from '../lib/joinPrefs';
import { navigate } from '../lib/router';
import { updateSettings, useSettings } from '../lib/settings';
import { saveOwnedMeeting } from '../lib/storage';

export function JoinDialog({ onClose, initial = '' }: { onClose: () => void; initial?: string }) {
  const settings = useSettings();
  const [value, setValue] = useState(initial);
  const [name, setName] = useState(settings.displayName);
  const [remember, setRemember] = useState(true);
  const [noAudio, setNoAudio] = useState(settings.joinMuted);
  const [noVideo, setNoVideo] = useState(settings.joinVideoOff);
  const [touched, setTouched] = useState(false);
  const parsed = useMemo(() => parseMeetingInput(value), [value]);
  const invalid = touched && value.trim() !== '' && !parsed;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!parsed || !name.trim()) return;
    if (remember) updateSettings({ displayName: name.trim() });
    setJoinPrefs({ name: name.trim(), audioMuted: noAudio, videoOff: noVideo });
    const q = parsed.passcode ? `?pwd=${encodeURIComponent(parsed.passcode)}` : '';
    navigate(`/j/${parsed.id}${q}`);
  };

  return (
    <Modal
      title="Unirse a una reunión"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="join-form" disabled={!parsed || !name.trim()}>
            Unirse
          </Button>
        </>
      }
    >
      <form id="join-form" onSubmit={submit} className="field" style={{ gap: 14 }}>
        <label className="field">
          <span>ID de reunión o enlace</span>
          <input
            className="input"
            autoFocus
            inputMode="text"
            placeholder="123 456 7890 o https://…/j/…"
            value={value}
            aria-invalid={invalid}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          {invalid ? <span className="error-text">Introduce un ID de 10 dígitos o pega el enlace de invitación.</span> : parsed && <span className="hint">Reunión {formatMeetingId(parsed.id)}</span>}
        </label>
        <label className="field">
          <span>Tu nombre</span>
          <input className="input" value={name} maxLength={LIMITS.nameLength} onChange={(e) => setName(e.target.value)} placeholder="Cómo te verán los demás" />
        </label>
        <label className="check">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
          Recordar mi nombre para futuras reuniones
        </label>
        <label className="check">
          <input type="checkbox" checked={noAudio} onChange={(e) => setNoAudio(e.target.checked)} />
          Entrar con el micrófono silenciado
        </label>
        <label className="check">
          <input type="checkbox" checked={noVideo} onChange={(e) => setNoVideo(e.target.checked)} />
          Desactivar mi vídeo
        </label>
      </form>
    </Modal>
  );
}

function defaultStart(): { date: string; time: string } {
  const d = new Date(Date.now() + 60 * 60_000);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
}

const DURATIONS = [15, 30, 45, 60, 90, 120, 180, 240, 480, 720];

export function ScheduleDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const start = defaultStart();
  const settings = useSettings();
  const [title, setTitle] = useState(settings.displayName ? `Reunión de ${settings.displayName}` : 'Mi reunión');
  const [date, setDate] = useState(start.date);
  const [time, setTime] = useState(start.time);
  const [duration, setDuration] = useState(60);
  const [usePasscode, setUsePasscode] = useState(true);
  const [passcode, setPasscode] = useState(randomPasscode);
  const [waitingRoom, setWaitingRoom] = useState(true);
  const [muteOnEntry, setMuteOnEntry] = useState(false);
  const [hostOnlyShare, setHostOnlyShare] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreateMeetingResponse | null>(null);

  const startDate = new Date(`${date}T${time}`);
  const inPast = startDate.getTime() < Date.now() - 5 * 60_000;
  const passcodeInvalid = usePasscode && !/^[\w-]{4,32}$/.test(passcode);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (inPast || passcodeInvalid || Number.isNaN(startDate.getTime())) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createMeeting({
        title,
        startTime: startDate.toISOString(),
        durationMinutes: duration,
        passcode: usePasscode ? passcode : '',
        settings: { waitingRoom, muteOnEntry, allowShare: hostOnlyShare ? 'host' : 'all' },
      });
      saveOwnedMeeting({
        id: res.meeting.id,
        hostKey: res.hostKey,
        passcode: res.passcode,
        title: res.meeting.title,
        startTime: res.meeting.startTime,
        durationMinutes: res.meeting.durationMinutes,
        createdAt: res.meeting.createdAt,
      });
      setCreated(res);
      onCreated();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    const source = { ...created.meeting, passcode: created.passcode };
    const text = inviteText(source, settings.displayName || undefined);
    return (
      <Modal
        title="Reunión programada"
        onClose={onClose}
        footer={
          <Button variant="primary" onClick={onClose}>
            Listo
          </Button>
        }
      >
        <div className="callout">
          <CalendarPlus size={18} />
          <span>
            <strong>{created.meeting.title}</strong> está lista. Comparte la invitación con los participantes.
          </span>
        </div>
        <div className="invite-box">{text}</div>
        <div className="row" style={{ flexWrap: 'wrap' }}>
          <Button onClick={() => void copyWithFeedback(text, 'invitación')}>
            <Copy size={16} /> Copiar invitación
          </Button>
          <Button onClick={() => void copyWithFeedback(joinUrl(created.meeting.id, created.passcode), 'enlace')}>
            <Link2 size={16} /> Copiar enlace
          </Button>
          <Button onClick={() => downloadIcs({ ...source, startTime: created.meeting.startTime! })}>
            <Download size={16} /> Añadir al calendario (.ics)
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Programar reunión"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="schedule-form" disabled={busy || inPast || passcodeInvalid || !title.trim()}>
            {busy ? 'Guardando…' : 'Guardar'}
          </Button>
        </>
      }
    >
      <form id="schedule-form" onSubmit={submit} className="field" style={{ gap: 14 }}>
        <label className="field">
          <span>Tema</span>
          <input className="input" value={title} maxLength={LIMITS.titleLength} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </label>
        <div className="grid-2">
          <label className="field">
            <span>Fecha</span>
            <input className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="field">
            <span>Hora</span>
            <input className="input" type="time" value={time} onChange={(e) => setTime(e.target.value)} required />
          </label>
        </div>
        {inPast && <span className="error-text">La fecha de inicio ya ha pasado.</span>}
        <label className="field">
          <span>Duración prevista</span>
          <select className="select" value={duration} onChange={(e) => setDuration(Number(e.target.value))}>
            {DURATIONS.map((d) => (
              <option key={d} value={d}>
                {d < 60 ? `${d} minutos` : `${d / 60} ${d === 60 ? 'hora' : 'horas'}`}
              </option>
            ))}
          </select>
          <span className="hint">Solo orientativa para la invitación: la reunión no se corta al terminar este tiempo.</span>
        </label>
        <div className="field">
          <span className="label">Seguridad</span>
          <label className="check">
            <input type="checkbox" checked={usePasscode} onChange={(e) => setUsePasscode(e.target.checked)} />
            Requerir código de acceso
          </label>
          {usePasscode && (
            <input className="input" value={passcode} aria-invalid={passcodeInvalid} aria-label="Código de acceso" onChange={(e) => setPasscode(e.target.value)} />
          )}
          {passcodeInvalid && <span className="error-text">Entre 4 y 32 letras, números, guiones o guiones bajos.</span>}
          <label className="check">
            <input type="checkbox" checked={waitingRoom} onChange={(e) => setWaitingRoom(e.target.checked)} />
            Sala de espera (tú decides quién entra)
          </label>
        </div>
        <div className="field">
          <span className="label">Opciones</span>
          <label className="check">
            <input type="checkbox" checked={muteOnEntry} onChange={(e) => setMuteOnEntry(e.target.checked)} />
            Silenciar a los participantes al entrar
          </label>
          <label className="check">
            <input type="checkbox" checked={hostOnlyShare} onChange={(e) => setHostOnlyShare(e.target.checked)} />
            Solo el anfitrión puede compartir pantalla
          </label>
        </div>
        {error && <div className="callout callout-error">{error}</div>}
      </form>
    </Modal>
  );
}

function randomPasscode(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(6)), (b) => alphabet[b % alphabet.length]).join('');
}
